import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { FileDown, FileText, Image as ImageIcon, Bug, AlertTriangle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmtBytes = (n) => { if (!n) return "0 B"; const u=["B","KB","MB","GB"]; let v=n,i=0; while(v>=1024&&i<u.length-1){v/=1024;i++;} return `${v.toFixed(v>=10||i===0?0:1)} ${u[i]}`; };
const fmtDate = (s) => s ? new Date(s).toLocaleString() : "—";

export default function SharedReport() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/shared-reports/${token}`);
        setState({ loading: false, data });
      } catch (err) {
        const code = err.response?.status;
        setState({
          loading: false,
          error: code === 410 ? "This share link has expired." :
                 code === 403 ? "The author has set this report to private." :
                 "Report not found or revoked.",
        });
      }
    })();
  }, [token]);

  const downloadPdf = () => { window.open(`${API}/shared-reports/${token}.pdf`, "_blank"); };

  if (state.loading) return <Shell><p className="text-sm text-slate-500">Loading report…</p></Shell>;
  if (state.error) return <Shell><div className="text-center py-16"><div className="text-2xl font-display font-bold text-slate-900 mb-2">Unavailable</div><p className="text-sm text-slate-500">{state.error}</p></div></Shell>;

  const { auto, report } = state.data;
  const t = auto.task;

  return (
    <Shell>
      <div className="flex items-start justify-between gap-4 mb-6 flex-col sm:flex-row" data-testid="shared-header">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Task report</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 leading-tight" data-testid="shared-task-title">{t.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{auto.project.name} · {auto.project.client_name || "Internal"} · Status <b className="text-slate-700">{t.status}</b></p>
        </div>
        <Button onClick={downloadPdf} variant="outline" className="border-slate-300" data-testid="btn-shared-pdf">
          <FileDown className="w-4 h-4 mr-1.5"/> Download PDF
        </Button>
      </div>

      {report?.summary && (
        <Section title="Executive summary" testid="shared-summary">
          <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">{report.summary}</p>
        </Section>
      )}

      {report?.narrative_html && (
        <Section title="Findings & narrative" testid="shared-narrative">
          {/* Server sanitises before storing — render trusted */}
          <div className="tiptap-content" dangerouslySetInnerHTML={{ __html: report.narrative_html }}/>
        </Section>
      )}

      <Section title="Task details" testid="shared-facts">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Fact label="Tester" value={auto.tester?.name}/>
          <Fact label="Reviewer" value={auto.reviewer?.name}/>
          <Fact label="Type" value={t.task_type}/>
          <Fact label="Environment" value={t.test_environment}/>
          <Fact label="Devices" value={t.device_requirements}/>
          <Fact label="Allocated hours" value={t.allocated_hours}/>
          <Fact label="Submitted" value={fmtDate(t.submitted_at)}/>
          <Fact label="Reviewed" value={fmtDate(t.reviewed_at)}/>
          <Fact label="Quality score" value={t.quality_score ?? "—"}/>
          <Fact label="Manager rating" value={auto.avg_rating ? `${auto.avg_rating}/5` : "—"}/>
        </dl>
      </Section>

      {t.submission_notes && (
        <Section title="Tester submission notes" testid="shared-notes">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{t.submission_notes}</p>
        </Section>
      )}

      {auto.evidence_files?.length > 0 && (
        <Section title={`Evidence files (${auto.evidence_files.length})`} testid="shared-evidence">
          <ul className="space-y-1.5 text-sm">
            {auto.evidence_files.map(f => (
              <li key={f.id} className="flex items-center justify-between">
                <a href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" className="text-cyan-700 hover:underline flex items-center gap-1.5">
                  {(f.mime||"").startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5"/> : <FileText className="w-3.5 h-3.5"/>}
                  {f.name}
                </a>
                <span className="text-xs text-slate-400">{fmtBytes(f.size)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {auto.issues?.length > 0 && (
        <Section title={`Linked issues (${auto.issues.length})`} testid="shared-issues">
          <ul className="space-y-3">
            {auto.issues.map(i => (
              <li key={i.id} className="border border-slate-200 rounded p-3">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5"><Bug className="w-3.5 h-3.5 text-rose-600"/>{i.title}</div>
                  <span className="text-[11px] text-slate-500">{i.severity || "—"} · {i.status || "—"}</span>
                </div>
                {i.description && <p className="text-sm text-slate-600">{i.description}</p>}
                {i.attachment_files?.length > 0 && (
                  <div className="text-[11px] text-slate-500 mt-2">Attachments: {i.attachment_files.map(f => f.name).join(", ")}</div>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {auto.decline_history?.length > 0 && (
        <Section title={`Decline history (${auto.decline_history.length})`} testid="shared-declines">
          <ul className="space-y-1 text-sm text-slate-600">
            {auto.decline_history.map((d, idx) => (
              <li key={idx} className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5"/>{fmtDate(d.at)} — {d.reason || "(no reason)"}</li>
            ))}
          </ul>
        </Section>
      )}

      {report?.recommendations && (
        <Section title="Recommendations" testid="shared-recos">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{report.recommendations}</p>
        </Section>
      )}

      <div className="text-[11px] text-slate-400 text-center mt-10 pb-6 flex items-center justify-center gap-1.5">
        <Clock className="w-3 h-3"/> Generated {fmtDate(report.updated_at)} · CogniTrust AI · This view will expire on {fmtDate(report.share_expires_at)}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-cyan-600"/>
          <span className="font-display font-bold text-slate-900">CogniTrust AI</span>
          <span className="text-xs text-slate-500 ml-1">Shared task report</span>
        </div>
      </div>
      <main className="max-w-3xl mx-auto px-4 py-8">{children}</main>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <section className="mb-6" data-testid={testid}>
      <h2 className="font-display font-semibold text-slate-900 text-base mb-2">{title}</h2>
      <div className="bg-white border border-slate-200 rounded-md p-4">{children}</div>
    </section>
  );
}

function Fact({ label, value }) {
  return (
    <>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-slate-900 font-medium">{value || "—"}</dd>
    </>
  );
}
